<template>
  <div v-if="state === 'loading'" class="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>

  <div v-else-if="state === 'editing'" class="flex items-center gap-2">
    <input
      ref="input"
      v-model="editValue"
      type="text"
      placeholder="Preferred name"
      class="sensitive px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark w-48"
      @keydown.enter="save"
      @keydown.escape="cancel"
    >
    <button
      :disabled="saving"
      class="btn-primary-sm"
      @click="save"
    >
      <FontAwesomeIcon v-if="saving" :icon="['fas', 'spinner']" spin />
      <FontAwesomeIcon v-else :icon="['fas', 'check']" />
    </button>
    <button
      class="px-2 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
      @click="cancel"
    >
      <FontAwesomeIcon :icon="['fas', 'times']" />
    </button>
    <span v-if="error" class="text-xs text-red-600">{{ error }}</span>
  </div>

  <span
    v-else-if="state === 'viewing'"
    class="sensitive relative inline-flex items-center gap-1.5 text-sm text-gray-600"
    @mouseenter="showInfo = true"
    @mouseleave="showInfo = false"
  >
    <FontAwesomeIcon v-if="currentDataSource" :icon="['fas', 'info-circle']" class="text-xs text-gray-300" />
    <span class="text-gray-500 font-medium">Preferred Name:</span>
    <span class="sensitive" data-spoof-kind="person-first">{{ currentValue }}</span>
    <button
      class="text-gray-300 hover:text-gray-500 transition-colors"
      @click="startEdit"
    >
      <FontAwesomeIcon :icon="['fas', 'pencil']" class="text-xs" />
    </button>
    <Teleport to="body">
      <div
        v-if="showInfo && currentDataSource"
        ref="tooltip"
        class="fixed z-50 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg whitespace-nowrap"
        :style="tooltipStyle"
      >
        <span class="block">Source: <span class="sensitive">{{ currentDataSource }}</span></span>
        <span v-if="currentUpdated" class="block text-gray-300">Updated: <span class="sensitive">{{ new Date(currentUpdated).toLocaleDateString() }}</span></span>
      </div>
    </Teleport>
  </span>

  <button
    v-else-if="state === 'empty'"
    class="text-xs text-gray-400 hover:text-gray-600 transition-colors"
    @click="startEdit"
  >
    <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />Add preferred name
  </button>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'PreferredNameField',
  components: { FontAwesomeIcon },
  props: {
    entityId: { type: [String, Number], required: true },
    attribute: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  emits: ['updated'],
  data() {
    return {
      editing: false,
      editValue: '',
      saving: false,
      error: null,
      showInfo: false,
      tooltipStyle: {},
      currentValue: null,
      currentDataSource: null,
      currentUpdated: null,
    };
  },
  computed: {
    state() {
      if (this.loading) return 'loading';
      if (this.editing) return 'editing';
      if (this.currentValue) return 'viewing';
      if (this.attribute) return 'empty';
      return 'hidden';
    },
  },
  watch: {
    showInfo(val) {
      if (val) {
        this.$nextTick(() => {
          const rect = this.$el.getBoundingClientRect();
          this.tooltipStyle = {
            top: `${rect.bottom + 6}px`,
            left: `${rect.left}px`,
          };
        });
      }
    },
    attribute: {
      immediate: true,
      handler(attr) {
        if (!attr) return;
        this.currentValue = attr.value || null;
        this.currentDataSource = attr.data_source || null;
        this.currentUpdated = attr.updated || null;
        this.editing = false;
      },
    },
  },
  methods: {
    startEdit() {
      this.editValue = this.currentValue || '';
      this.editing = true;
      this.error = null;
      this.$nextTick(() => this.$refs.input?.focus());
    },
    cancel() {
      this.editing = false;
      this.error = null;
    },
    async save() {
      this.saving = true;
      this.error = null;
      try {
        const { data } = await axios.post(
          `/api/entities/${this.entityId}/update_field/`,
          {
            field_name: 'preferred_name',
            field_value: this.editValue || null,
            data_source_name: 'Manual Update',
            reason: 'Updated via UI',
          },
        );
        this.currentValue = this.editValue || null;
        this.currentDataSource = 'Manual Update';
        this.currentUpdated = data.updated_at;
        this.editing = false;
        this.$emit('updated', this.currentValue);
      } catch {
        this.error = 'Failed to save';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
